import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { stepperTotal } from '@/components/AleloStepper'
import { executeSafely } from '@/utils/helpers'
import * as verificationApi from '@/utils/signupVerificationApi'
import type {
  ConfigResult,
  StartResult,
  VerificationChannel,
} from '@/utils/signupVerificationApi'
import { describeConfigError, describeOtpError } from './otpErrors'
import { useVerificationChannels } from './useVerificationChannels'

/** Texto de cada sub-etapa de confirmação. */
export const OTP_COPY: Record<
  VerificationChannel,
  { title: string; description: string; submit: string }
> = {
  email: {
    title: 'Confirme seu e-mail',
    description: 'Digite o código que enviamos para',
    submit: 'CONFIRMAR E-MAIL',
  },
  sms: {
    title: 'Confirme seu celular',
    description: 'Digite o código que enviamos por SMS para',
    submit: 'CONFIRMAR CELULAR',
  },
}

export { describeOtpError } from './otpErrors'

export const RESEND_SECONDS = 60

export type VerificationHandlers<T> = {
  /** Pede ao BFF o código do canal (ou o token, quando nenhum canal é exigido). */
  start: (
    context: T,
    options: { channel: VerificationChannel; sessionId?: string },
  ) => Promise<StartResult>
  /** Só aqui `instance.signup()` é chamado, com o token no payload. */
  complete: (context: T, token: string) => Promise<void>
  /** Mensagem para erros lançados pelos handlers (validações da tela). */
  describeError: (err: unknown) => string
  /** Mensagem genérica da tela para erros sem tradução própria. */
  failed: string
}

/**
 * Máquina de etapas do cadastro: `form → [otp-email] → [otp-sms]`, com cada
 * sub-etapa condicionada aos canais que o BFF exige. `context` é o que a tela
 * precisa preservar entre as etapas (o formulário, a senha); ele só chega a
 * `complete` depois de o BFF emitir o token.
 */
export function useSignupVerification<T>(handlers: VerificationHandlers<T>) {
  const [channels, setChannels] = useVerificationChannels()
  const [step, setStep] = useState<'form' | 'otp'>('form')
  const [channelIndex, setChannelIndex] = useState(0)
  const [sessionId, setSessionId] = useState('')
  const [submitted, setSubmitted] = useState<T | null>(null)
  const [code, setCode] = useState('')
  const [resendSeconds, setResendSeconds] = useState(0)
  const [error, setError] = useState<string>()
  const [pending, setPending] = useState(false)

  const otpError = (errorCode: string) => describeOtpError(errorCode, handlers.failed)

  useEffect(() => {
    if (resendSeconds <= 0) return
    const timer = window.setTimeout(() => setResendSeconds(resendSeconds - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [resendSeconds])

  /** A config é fonte única: se ainda não veio, tenta de novo antes de começar. */
  const loadChannels = useCallback(async (): Promise<ConfigResult> => {
    if (channels) return { ok: true, channels }
    const result = await verificationApi.config()
    if (result.ok) setChannels(result.channels)
    return result
  }, [channels])

  /** Primeira metade: valida (nos handlers), pede o 1º código ou conclui direto. */
  async function begin(context: T) {
    setError(undefined)
    setPending(true)
    try {
      const loaded = await loadChannels()
      if (!loaded.ok) {
        setError(describeConfigError(loaded.error))
        return
      }
      const required = loaded.channels

      const started = await executeSafely(() =>
        handlers.start(context, { channel: required[0] }),
      )
      if (!started) return
      if (!started.ok) {
        setError(otpError(started.error))
        return
      }

      // Nenhum canal exigido: o BFF já devolveu o token, não há sub-etapa.
      if (started.verified) {
        await executeSafely(() => handlers.complete(context, started.token))
        return
      }

      setSubmitted(context)
      setSessionId(started.sessionId)
      setChannelIndex(0)
      setCode('')
      setResendSeconds(RESEND_SECONDS)
      setStep('otp')
    } catch (err) {
      setError(handlers.describeError(err))
    } finally {
      setPending(false)
    }
  }

  /** Confirma o código da sub-etapa atual e avança, ou cria a conta. */
  async function confirm(event: FormEvent) {
    event.preventDefault()
    setError(undefined)

    const context = submitted
    const required = channels
    if (context === null || !required) return
    const channel = required[channelIndex]

    setPending(true)
    try {
      const result = await verificationApi.confirm({ sessionId, channel, code })
      if (!result.ok) {
        setError(otpError(result.error))
        return
      }

      if (result.verified) {
        await executeSafely(() => handlers.complete(context, result.token))
        return
      }

      const next = required[channelIndex + 1]
      if (!next) {
        setError(handlers.failed)
        return
      }

      const started = await executeSafely(() =>
        handlers.start(context, { channel: next, sessionId }),
      )
      if (!started) return
      if (!started.ok) {
        setError(otpError(started.error))
        return
      }
      setChannelIndex(channelIndex + 1)
      setCode('')
      setResendSeconds(RESEND_SECONDS)
    } catch (err) {
      setError(handlers.describeError(err))
    } finally {
      setPending(false)
    }
  }

  /** Reenvio do código da sub-etapa atual — o throttle vive no BFF. */
  async function resend() {
    const context = submitted
    const required = channels
    if (context === null || !required || pending || resendSeconds > 0) return

    setError(undefined)
    setPending(true)
    try {
      const started = await executeSafely(() =>
        handlers.start(context, { channel: required[channelIndex], sessionId }),
      )
      if (started && !started.ok) {
        setError(otpError(started.error))
        return
      }
      setResendSeconds(RESEND_SECONDS)
    } catch (err) {
      setError(handlers.describeError(err))
    } finally {
      setPending(false)
    }
  }

  const totalSteps = stepperTotal(channels)
  const currentChannel =
    step === 'otp' && channels ? channels[channelIndex] : undefined

  return {
    channels,
    step,
    submitted,
    currentChannel,
    /** 1 = dados; cada canal confirmado soma 1. */
    currentStep: step === 'otp' ? channelIndex + 2 : 1,
    totalSteps,
    code,
    setCode,
    resendSeconds,
    error,
    setError,
    pending,
    setPending,
    begin,
    confirm,
    resend,
  }
}
